import { defineStore } from 'pinia';
import { Light, LightApi } from '@/types/Light';
import { LightMapper } from '@/utils/mappers/LightMapper';
import { CameraSettings, CameraSettingsApi } from '@/types/CameraSettings';
import { CameraSettingsMapper } from '@/utils/mappers/CameraSettingsMapper';
import { defaultCameraSettings } from '@/data/defaultCameraSettings';
import { useSiteStore } from './site';
import { useCanvasStore } from './canvas';
import { Camera, Raycaster, Scene, WebGLRenderer } from 'three';

interface Context {
    camera?: Camera;
    renderer?: WebGLRenderer;
    scene?: Scene;
    raycaster?: Raycaster;
    [key: string]: any;
}

export const useSceneStore = defineStore('scene', {
    state: () => ({
        render2D: true as boolean,
        lights: [] as Light[],
        cameraSettings: CameraSettingsMapper.getCameraSettingsFromData(defaultCameraSettings) as CameraSettings,
        // Pose caméra INITIALE (position/target du JSON produit au chargement), figée :
        // le bouton « Recentrer » y revient. cameraSettings.position peut être mutée en
        // cours d'usage (focus zone) — ce snapshot ne l'est pas.
        initialCameraPose: null as null | {
            position: { x: number; y: number; z: number };
            target: { x: number; y: number; z: number };
        },
        // Positions caméra des 3 screenshots (config produit BO). null => LEGACY
        // (rotation objet 120°). Renseigné SEULEMENT si les 3 sont valides
        // (tout ou rien) — cf. setScreenshotSettings.
        screenshotShots: null as null | Array<{
            position: { x: number; y: number; z: number };
            target: { x: number; y: number; z: number };
        }>,
        context: {} as Context
    }),
    actions: {
        setLights(lightData: LightApi[]) {
            this.lights = [];
            if (lightData.length === 0) {
                this.lights = defaultLightList.map((light) => LightMapper.getLightFromData(light.object));
                console.warn('No lights found in the scene, using default lights');
                return;
            }
            this.lights = lightData.map((light) => LightMapper.getLightFromData(light));
        },
        setCameraSettings(cameraSettingsData: CameraSettingsApi) {
            if (!cameraSettingsData || Object.keys(cameraSettingsData).length === 0) {
                this.cameraSettings = CameraSettingsMapper.getCameraSettingsFromData(defaultCameraSettings);
                console.warn('No camera settings provided, using default camera settings');
            } else {
                this.cameraSettings = CameraSettingsMapper.getCameraSettingsFromData(cameraSettingsData);
            }
            // Fige la pose initiale (le recentrage y revient).
            const p: any = this.cameraSettings.position ?? { x: 0, y: 0, z: 0 };
            const t: any = (this.cameraSettings as any).target ?? { x: 0, y: 0, z: 0 };
            this.initialCameraPose = {
                position: { x: p.x, y: p.y, z: p.z },
                target: { x: t.x, y: t.y, z: t.z }
            };
        },
        /**
         * Positions caméra des 3 screenshots (produit.screenshotSettings du BO).
         * TOUT OU RIEN : on ne retient que si les 3 slots sont présents ET valides
         * (chacun avec une position numérique) ; sinon `null` => comportement
         * LEGACY (rotation objet 120°) côté Screenshot.js.
         */
        setScreenshotSettings(data: { shots?: Array<any> } | null | undefined) {
            const shots = data?.shots;
            const isValid = (s: any) =>
                s && s.position && ['x', 'y', 'z'].every((k) => typeof s.position[k] === 'number');
            if (Array.isArray(shots) && shots.length >= 3 && shots.slice(0, 3).every(isValid)) {
                this.screenshotShots = shots.slice(0, 3).map((s) => ({
                    position: { x: s.position.x, y: s.position.y, z: s.position.z },
                    target: {
                        x: s.target?.x ?? 0,
                        y: s.target?.y ?? 0,
                        z: s.target?.z ?? 0
                    }
                }));
            } else {
                this.screenshotShots = null;
            }
        },
        setContext(camera: Camera, renderer: WebGLRenderer, scene: Scene, raycaster: Raycaster) {
            this.context.camera = camera;
            this.context.renderer = renderer;
            this.context.scene = scene;
            this.context.raycaster = raycaster;
        },
        renderAll() {
            this.render2D = true;
            const canvasStore = useCanvasStore();
            if (
                this.context &&
                this.context.camera &&
                this.context.renderer &&
                this.context.scene &&
                canvasStore.isCanvasTextureMounted
            ) {
                this.context.renderer.render(this.context.scene, this.context.camera);
            }
        }
    },
    getters: {
        backgroundColor: () => {
            const siteStore = useSiteStore();
            return siteStore.site.sceneBackgroundColor || undefined;
        }
    }
});
